import React, { useId, useEffect, useState, useCallback } from 'react'
import { Alert, Form, Spinner } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import apiClient from '../../utils/api/apiClient'
import { APIEndpoints } from '../../utils/api/apiConfig'
import { useCancelToken } from '../../utils/hooks/useCancelToken'
import SearchField from '../Forms/SearchField'
import Article from './Article'
import ArticleDND from './ArticleDND'
import LazyLoad from '../Forms/LazyLoad'
import SearchResultAmount from '../Forms/SearchResultAmount'
import { makeCall } from '../../utils/api/makeCall'
import useMountEffect from '../../utils/hooks/useMountEffect'

const ArticleSelect = ({
  selectedArticles,
  setSelectedArticles,
  hideArticles,
  withDND,
  getArticleAssignments,
  listRef,
  singleSelection,
  officeIndependent = true,
  localArticleDependent = false,
  userOffices = false,
  maintenance = false,
}) => {
  const [fetchedArticles, setFetchedArticles] = useState([])
  const [searchText, setSearchText] = useState('')
  const [offset, setOffset] = useState(0)
  const [totalCount, setTotalCount] = useState(0)
  const { t } = useTranslation()
  const { createOrCancelToken, sourceRef, isCancel } = useCancelToken()
  const limit = 10
  const [lastClickedArticleId, setLastClickedArticleId] = useState(null)
  const [allowRange, setAllowRange] = useState(false)
  const [allSelected, setAllSelected] = useState(false)
  // here we are checking if there are already selected articles for the Contigent templates modal user
  const filteredArray = hideArticles
    ? fetchedArticles.filter(
        (item) => !hideArticles.find((element) => element === item.article.id)
      )
    : fetchedArticles
  const [, setErrorStates] = useState({})
  const [loadingStates, setLoadingStates] = useState({})
  const [startWithSelectAll, setStartWithSelectAll] = useState(false)
  // eslint-disable-next-line
  const [windowWidth, setWindowWidth] = useState(window.innerWidth)

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  const handleChangeSearchText = async (newSearchText) => {
    setAllSelected(false)
    setSelectedArticles([])
    setLastClickedArticleId(null)
    setSearchText(newSearchText)
    fetchArticles({ newOffset: 0, newSearch: newSearchText })
  }

  const fetchArticles = async (
    newParams = { newOffset: offset, newSearch: searchText }
  ) => {
    createOrCancelToken()
    let append = !!newParams.newOffset
    const params = getParamsForArticlesRequest(newParams)
    makeCall(
      'getArticles',
      async () => {
        let response = await apiClient.getJson(
          APIEndpoints.articles,
          params,
          true,
          true,
          true,
          { cancelToken: sourceRef.current.token }
        )
        setFetchedArticles((prev) =>
          append ? [...prev, ...response.data] : response.data
        )
        const total = parseInt(response.headers['x-total-count'])
        if (!isNaN(total)) {
          setTotalCount(total)
        }
        setOffset(newParams.newOffset + limit)
      },
      setErrorStates,
      setLoadingStates,
      isCancel
    )
  }

  const checkSessionStorage = async () => {
    if (sessionStorage.getItem('searchArticleNumber')) {
      let articleNumber = JSON.parse(
        sessionStorage.getItem('searchArticleNumber')
      )
      sessionStorage.removeItem('searchArticleNumber')
      if (articleNumber) {
        await handleChangeSearchText(articleNumber)
        setStartWithSelectAll(true)
      }
    }
  }
  useMountEffect(() => {
    checkSessionStorage()
  })

  const getParamsForArticlesRequest = (newParams) => {
    const { newOffset, newSearch } = newParams
    return {
      'filter.searchText': newSearch ? newSearch : null,
      'page.offset': newOffset,
      'page.limit': limit,
      'filter.officeIndependent': !!officeIndependent,
      'filter.localArticleDependent': !!localArticleDependent,
      'filter.userOffices': !!userOffices,
      'filter.maintenance': !!maintenance,
    }
  }

  function getArticlesInRange(lastClickedId, newId) {
    let lastIndex = filteredArray.findIndex(
      (article) => article.article.id === lastClickedId
    )
    let newIndex = filteredArray.findIndex(
      (article) => article.article.id === newId
    )
    if (lastIndex < newIndex) {
      return filteredArray.slice(lastIndex, newIndex + 1)
    } else if (lastIndex > newIndex) {
      return filteredArray.slice(newIndex, lastIndex + 1)
    } else return []
  }

  const onClickSelectedArticles = (article) => {
    setAllSelected(false)
    let selectedArticleId = article.article.id
    setLastClickedArticleId(selectedArticleId)
    let newSelectedArticles = [...selectedArticles]
    let existingArticleIndex = selectedArticles.findIndex(
      (article) => article.article.id === selectedArticleId
    )
    if (allowRange) {
      let articlesInRange = getArticlesInRange(
        lastClickedArticleId,
        selectedArticleId
      )
      //lastClickedArticleId is selected atm
      if (
        selectedArticles.find(
          (article) => article.article.id === lastClickedArticleId
        )
      ) {
        //add articles
        setSelectedArticles([
          ...new Set([...newSelectedArticles, ...articlesInRange]),
        ])
      }
    } else if (existingArticleIndex >= 0) {
      //remove article if already selected
      newSelectedArticles.splice(existingArticleIndex, 1)
      setSelectedArticles(newSelectedArticles)
    } else {
      if (singleSelection) newSelectedArticles = []
      newSelectedArticles.push(article)
      setSelectedArticles(newSelectedArticles)
    }
  }

  useEffect(() => {
    let listRefCurrent = listRef?.current
    const keypressShiftCallback = (e) => {
      if (!singleSelection && e.shiftKey && lastClickedArticleId) {
        setAllowRange(true)
      } else {
        setAllowRange(false)
      }
    }
    if (listRefCurrent) {
      listRefCurrent.addEventListener('click', keypressShiftCallback)
    }
    return () => {
      listRefCurrent &&
        listRefCurrent.removeEventListener('click', keypressShiftCallback)
    }
  }, [lastClickedArticleId, listRef, singleSelection])

  const setAllArticles = useCallback(async () => {
    var articles = []
    const params = {
      'filter.searchText': searchText,
      'filter.officeIndependent': !!officeIndependent,
      'filter.localArticleDependent': !!localArticleDependent,
      'filter.userOffices': !!userOffices,
      'filter.maintenance': !!maintenance,
    }
    await makeCall(
      'getAllArticles',
      async () => {
        articles = await apiClient.getJson(APIEndpoints.articles, params)
      },
      setErrorStates,
      setLoadingStates,
      isCancel
    )
    return articles
  }, [
    searchText,
    officeIndependent,
    localArticleDependent,
    userOffices,
    maintenance,
    isCancel,
  ])

  const selectAll = useCallback(async () => {
    var articles = []
    if (!allSelected) {
      articles = await setAllArticles()
    }
    setSelectedArticles(articles)
    setAllSelected((prev) => !prev)
  }, [allSelected, setSelectedArticles, setAllSelected, setAllArticles])
  useEffect(() => {
    if (startWithSelectAll && searchText) {
      setStartWithSelectAll(false)
      selectAll()
    }
  }, [startWithSelectAll, searchText, selectAll])
  const checkboxId = useId()
  return (
    <>
      {/* {windowWidth >= 476 && ( */}
      <Form.Group className='mb-3 p-2'>
        <Form.Label>{t('ProductOverview:searchArticle')}</Form.Label>
        <SearchField
          placeholder={t('ProductOverview:searchArticle')}
          value={searchText}
          onChange={(e) => handleChangeSearchText(e.target.value)}
          onClearClick={() => handleChangeSearchText('')}
        />
        <div className='d-flex justify-content-between'>
          {/* LEFT: Result amount only if valid */}
          {searchText && typeof totalCount === 'number' && totalCount > 0 ? (
            <SearchResultAmount count={totalCount} />
          ) : (
            <div className='mt-2'>&nbsp;</div>
          )}

          {/* RIGHT: Spinner or checkbox */}
          {loadingStates.getAllArticles ? (
            <Spinner
              size='sm'
              className='mt-2'
              animation='border'
              variant='primary'
            />
          ) : (
            filteredArray.length > 0 &&
            !singleSelection && (
              <Form.Check
                className='mt-2'
                label={t('order:selectAll')}
                id={checkboxId}
                checked={allSelected}
                onChange={selectAll}
              />
            )
          )}
        </div>
      </Form.Group>
      {/* )} */}
      {filteredArray.map((article, index) =>
        withDND ? (
          <ArticleDND
            key={article.article.id}
            onClickSelectedArticles={onClickSelectedArticles}
            selectedArticles={selectedArticles}
            article={article}
            index={index}
            getArticleAssignments={getArticleAssignments}
          />
        ) : (
          <Article
            key={article.article.id}
            onClickSelectedArticles={onClickSelectedArticles}
            selectedArticles={selectedArticles}
            article={article}
            index={index}
          />
        )
      )}
      {searchText && !loadingStates.getArticles && !filteredArray.length && (
        <>
          <Alert variant='info'>
            {t('productsOverview:noProductsAvailable')}
          </Alert>
        </>
      )}
      {totalCount > offset && !loadingStates.getArticles ? (
        <LazyLoad fetchData={fetchArticles} listRef={listRef} />
      ) : (
        ''
      )}
      {loadingStates.getArticles && (
        <div className='d-flex justify-content-center p-3'>
          <Spinner animation='border' variant='primary' />
        </div>
      )}
    </>
  )
}

export default ArticleSelect
